import * as THREE from 'three';

const DEFAULT_FAR = 24;

/**
 * Small allocation-conscious wrapper around THREE.Raycaster.
 *
 * The returned intersection objects belong to an internal buffer and are only
 * guaranteed to remain valid until the next call to intersect()/first().
 */
export class Raycaster {
  constructor({ near = 0.02, far = DEFAULT_FAR, recursive = false } = {}) {
    this.raycaster = new THREE.Raycaster();
    this.raycaster.near = near;
    this.raycaster.far = far;
    this.recursive = recursive;

    this.targets = [];
    this.intersections = [];
    this.pointer = new THREE.Vector2();

    this._origin = new THREE.Vector3();
    this._direction = new THREE.Vector3();
    this._rotation = new THREE.Matrix4();
  }

  setTargets(targets) {
    this.targets = targets ?? [];
    return this;
  }

  setFromController(controller) {
    const source = controller?.controller ?? controller;

    if (!source?.matrixWorld) {
      return false;
    }

    source.updateWorldMatrix?.(true, false);
    this._rotation.identity().extractRotation(source.matrixWorld);
    this._origin.setFromMatrixPosition(source.matrixWorld);
    this._direction.set(0, 0, -1).transformDirection(this._rotation);
    this.raycaster.set(this._origin, this._direction);
    return true;
  }

  setFromCamera(pointer, camera) {
    if (!camera) {
      return false;
    }

    this.pointer.copy(pointer);
    this.raycaster.setFromCamera(this.pointer, camera);
    return true;
  }

  setPointerFromEvent(event, domElement) {
    if (!domElement) {
      return this.pointer.set(0, 0);
    }

    const bounds = domElement.getBoundingClientRect();
    const width = Math.max(1, bounds.width);
    const height = Math.max(1, bounds.height);

    this.pointer.set(
      ((event.clientX - bounds.left) / width) * 2 - 1,
      -((event.clientY - bounds.top) / height) * 2 + 1,
    );

    return this.pointer;
  }

  intersect(targets = this.targets, recursive = this.recursive) {
    this.intersections.length = 0;
    this.raycaster.intersectObjects(targets, recursive, this.intersections);
    return this.intersections;
  }

  first(targets = this.targets, recursive = this.recursive) {
    const intersections = this.intersect(targets, recursive);
    return intersections.length > 0 ? intersections[0] : null;
  }
}

export default Raycaster;
